import endpoints from 'constants/api/Endpoints';
import { logError } from 'services/logService/errorLogger';
import { parseMaintenanceStatus } from 'maintenance/parse';
import { MaintenanceStatus } from 'maintenance/types';

const TAG = 'MaintenanceApiService';

/**
 * Fetches the scoped maintenance status for an area (or the whole-product
 * windows when no area is given).
 *
 * Fails open: any error, or a payload that cannot be understood, resolves to
 * `null` so callers show nothing rather than blocking visitors.
 *
 * @param {string} [area] The area slug, e.g. 'billing'. Omit for whole-product windows only.
 * @returns {Promise<MaintenanceStatus | null>} The parsed status, or null when unavailable.
 *
 * @example
 * const status = await fetchMaintenanceStatus('billing');
 * if (status?.blocking) { ... }
 */
export async function fetchMaintenanceStatus(area?: string): Promise<MaintenanceStatus | null> {
  try {
    const result = await endpoints.fetchMaintenanceStatus(area ? { tag: TAG, area } : { tag: TAG });
    if (!result.success) {
      logError('Failed to fetch maintenance status', { error: result.error, area }, TAG);
      return null;
    }
    return parseMaintenanceStatus(result.data);
  } catch (error) {
    logError('Error fetching maintenance status', { error, area }, TAG);
    return null;
  }
}
